import type { UniqueIdentifier } from '@dnd-kit/core';
import useOptimisticSnapshot, {
  idKey,
  type SnapshotEntry,
} from '../optimistic/useOptimisticSnapshot';
import type {
  SortableTreeBaseContainer,
  SortableTreeBaseItem,
  SortableTreeNode,
} from './types';
import { buildTree, flatten } from './utils';

type TreeSnapshotEntry = SnapshotEntry & {
  parentId: UniqueIdentifier | null;
};

const snapshot = <
  TItem extends SortableTreeBaseItem,
  TContainer extends SortableTreeBaseContainer,
>(
  nodes: SortableTreeNode<TItem, TContainer>[]
): TreeSnapshotEntry[] =>
  flatten(nodes).map((node) => ({
    id: node.id,
    parentId: node.parentId,
    key: `${idKey(node.id)} ${node.parentId == null ? '' : idKey(node.parentId)}`,
  }));

const reapply = <
  TItem extends SortableTreeBaseItem,
  TContainer extends SortableTreeBaseContainer,
>(
  target: TreeSnapshotEntry[],
  nodes: SortableTreeNode<TItem, TContainer>[]
): SortableTreeNode<TItem, TContainer>[] => {
  const byId = new Map(flatten(nodes).map((node) => [node.id, node]));
  // Re-nest the live nodes by the pending structure, so an edit to a node's
  // payload made while the move is in flight still flows through.
  return buildTree(
    target.map((entry) => ({
      ...byId.get(entry.id)!,
      parentId: entry.parentId,
    }))
  );
};

/**
 * Tree analog of `useOptimisticReorder`. Lets {@link SortableTree} show the
 * result of a move immediately while it waits for the new tree to flow back
 * through the `items` prop (e.g. via an async TanStack Query update).
 *
 * Call `commit(nextTree)` after a drop to record the optimistic *structure*
 * (each node's `parentId` + order) and render from `displayItems` instead of
 * the raw `items` prop. `displayItems` re-nests the live `items` by that pending
 * structure, so an in-flight edit to a node's payload still flows through.
 *
 * The pending structure auto-clears when the `items` prop catches up, when it
 * settles on a different structure (which then wins — e.g. a rejected or
 * normalised move), or when the id set diverges. To cancel a move, the consumer
 * returns `false` from `SortableTree`'s `onChange` so `commit` is never called.
 * See {@link useOptimisticSnapshot} for the shared mechanism.
 */
const useOptimisticTree = <
  TItem extends SortableTreeBaseItem,
  TContainer extends SortableTreeBaseContainer = SortableTreeBaseContainer,
>(
  items: SortableTreeNode<TItem, TContainer>[]
) =>
  useOptimisticSnapshot<
    SortableTreeNode<TItem, TContainer>[],
    TreeSnapshotEntry
  >(items, { snapshot, reapply });

export default useOptimisticTree;
